import { useEffect, useMemo, useState, useContext } from 'react';
import { Tree, Divider, Tag, Button, Select } from 'antd';
import { pickBy, identity, isEmpty } from 'lodash'
import classnames from 'classnames';
import usePlatform from '../../hooks/usePlatform';
import Context from '../../container';
import PureVideoModal, { getMineType } from './pure-video-modal';
import Section from './section';
import IconAiArrow from '../../components/IconWrapper/arrow-ai';
import { getSceneInfo, getSceneFilter } from './services';
import './scene.scss';

const SORT_FILTER = [{name: 'Agent', key: 'agent'}, {name: '一级分类', key: 'firstRole'}, {name: '二级分类', key: 'secondRole'}];

type SceneLabel = {
  name?: string;
  pcLogo?: string;
};

type SceneItem = {
  title?: string;
  desc?: string;
  color?: string; // hex
  status?: 'COMING_SOON' | 'ONLINE' | string;
  pcResource?: string; // image or video url
  pcLink?: string;
  labelAiBO?: SceneLabel;
};

type SceneApiData = {
  title?: string;
  desc?: string;
  sceneList?: SceneItem[];
};

const LANDING_PAGE_DISPLAY_COUNT = 10;

function darkenColor(hexColor: string, percent: number): string {
  const hex = (hexColor || '#fbbf24').replace('#', '');
  const normalized = hex.length === 3 ? hex.split('').map((c) => c + c).join('') : hex;
  const r = Math.max(0, Math.min(255, Math.floor(parseInt(normalized.substring(0, 2), 16) * (1 - percent / 100))));
  const g = Math.max(0, Math.min(255, Math.floor(parseInt(normalized.substring(2, 4), 16) * (1 - percent / 100))));
  const b = Math.max(0, Math.min(255, Math.floor(parseInt(normalized.substring(4, 6), 16) * (1 - percent / 100))));
  const toHex = (v: number) => v.toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

interface SceneListProps {
  sceneInfo?: SceneApiData;
  onClearFilter: (key?: string) => void;
  selectedFilter: any;
  setSelectedFilter: (selectedFilter: any) => void;
  isLandingPage: boolean;
}
export const SceneList = ({ sceneInfo, onClearFilter, selectedFilter, setSelectedFilter, isLandingPage }: SceneListProps) => {
  const [videoSrc, setVideoSrc] = useState<string>('');

  const scenesToRender = useMemo(() => {
    const allScenes = sceneInfo?.sceneList || [];
    if (isLandingPage) {
      return allScenes.slice(0, LANDING_PAGE_DISPLAY_COUNT);
    }
    return allScenes;
  }, [sceneInfo?.sceneList, isLandingPage]);

  const hasMoreScenes = isLandingPage && (sceneInfo?.sceneList?.length || 0) > LANDING_PAGE_DISPLAY_COUNT;

  function openVideo(src: string) {
    setVideoSrc(src);
  }

  function closeVideo() {
    setVideoSrc('');
  }

  function renderCard(scene: SceneItem, index: number) {
    const isComing = scene.status === 'COMING_SOON';

    const resourceUrl = scene.pcResource || '';
    const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(resourceUrl);

    const content = (
      <div className="scene-card__content">
        <span>
          <div className="scene-card__role-tag">
            {scene.labelAiBO?.pcLogo ? (
              <img className="scene-card__role-logo" src={scene.labelAiBO?.pcLogo} alt="role" />
            ) : null}
            {scene.labelAiBO?.name || ''}
          </div>
        </span>
        <h3 className="scene-card__title">{scene.title || '暂无标题'}</h3>
        <p className="scene-card__desc">{scene.desc || '暂无描述'}</p>
        {isComing ? (
          <div>
            <div className="scene-card__coming">即将上线</div>
          </div>
        ) : null}
      </div>
    );

    const media = (
      <div className="scene-card__media">
        {isVideo ? (
          <div className="scene-card__video-container">
            <video className="scene-card__video" preload="metadata">
              <source src={resourceUrl} type={getMineType(resourceUrl)} />
            </video>
            <div className="scene-card__play" onClick={() => openVideo(resourceUrl)} />
          </div>
        ) : (
          <div className="scene-card__image-container">
            {/* 使用 picture 以便后续扩展 webp 兼容 */}
            <picture>
              {resourceUrl && <img className="scene-card__image" src={resourceUrl} alt={scene.title || '场景图片'} />}
            </picture>
          </div>
        )}
      </div>
    );

    const cardInner = (
      <div className="scene-card">
        {content}
        {media}
      </div>
    );

    if (scene.pcLink && !isComing) {
      return (
        <a key={`${index}-${scene.title}`} href={scene.pcLink} target="_blank" rel="noreferrer" className="scene-card__link">
          {cardInner}
        </a>
      );
    }
    return (
      <div key={`${index}-${scene.title}`} className="scene-card__link">
        {cardInner}
      </div>
    );
  }

  return (
    <>
      <div className="scene-list-wrapper">
        {!isEmpty(selectedFilter) && SORT_FILTER.find((s) => selectedFilter?.[s.key]?.key !== 'all') && <div className="scene-list-filter">
          {SORT_FILTER.map((s) => selectedFilter?.[s.key]?.key !== 'all' && <Tag closable onClose={() => onClearFilter(s.key)}>{selectedFilter?.[s.key]?.title}</Tag>)}
          <Button type='link' onClick={() => onClearFilter()} style={{ padding: 0 }}>清空所有</Button>
        </div>}

        <div className="scene-grid">
          {scenesToRender.map((s, i) => renderCard(s, i))}
        </div>

        {(sceneInfo?.sceneList?.length || 0) === 0 && (
          <div className="scene-more">
            <div className="scene-more__btn">
              更多场景即将上线,敬请期待...
            </div>
          </div>
        )}
        {hasMoreScenes && (
          <div className="scene-more">
            <a href="/ai/scene" className="scene-more__btn scene-more__btn--active">
              <span>点击探索更多场景</span>
              <IconAiArrow />
            </a>
          </div>
        )}
      </div>
      <PureVideoModal
        videoModalVisible={!!videoSrc}
        closeVideoModal={closeVideo}
        videoSrc={videoSrc}
      />
    </>
  );
}

interface FilterProps {
  selectedFilter: any;
  setSelectedFilter: (selectedFilter: any) => void;
  isPc: boolean;
}

const Filter = ({ selectedFilter, setSelectedFilter, isPc }: FilterProps) => {
  const [filterData, setFilterData] = useState<any>({
    agent: [],
    firstRole: [],
    secondRole: [],
  });


  useEffect(() => {
    getSceneFilter().then((res: any) => {
      setFilterData({
        agent: res.agentTypeBOS?.map((s: any) => ({ title: s.name, key: s.id, isLeaf: true })),
        firstRole: res.firstRoleTypeDTOS?.map((s: any) => ({ title: s.name, key: s.id, isLeaf: true })),
        secondRole: res.secondRoleTypeDTOS?.map((s: any) => ({ title: s.name, key: s.id, isLeaf: true })),
      });
    });
  }, []);

  if (!isPc) {
    return <div className="scene-filter-mobile-wrapper">
      {SORT_FILTER.map((s) => (
        <div className='scene-filter-mobile-column'>
          <div className="scene-filter-mobile-column-title">{s.name}</div>
          <div className="scene-filter-mobile-content">
            <Select
              value={selectedFilter?.[s.key]?.key}
              options={[{ title: '全部', key: 'all' }].concat(filterData[s.key]).map((s: any) => ({ label: s.title, value: s.key }))}
              onChange={(value) => {setSelectedFilter({ ...selectedFilter, [s.key]: { key: value, title: filterData[s.key].find((s: any) => s.key === value)?.title || '' } })}}
            />
          </div>
        </div>
      ))}
    </div>
  }
  return <div className="scene-filter-wrapper">
    {SORT_FILTER.map((s) => (
      <div key={s.key}>
        <div className="scene-filter-title">{s.name}</div>
        <Divider style={{ margin: '0.5rem 0' }}/>
        <Tree
          selectedKeys={[selectedFilter?.[s.key]?.key]}
          className='ant-tree-no-indent'
          blockNode
          height={250}
          treeData={[{ title: '全部', key: 'all' }].concat(filterData[s.key])}
          onSelect={(keys: any, info: any) => {
            setSelectedFilter({ ...selectedFilter, [s.key]: { key: keys[0], title: info.node.title } });
          }}
        />
      </div>
  ))}
  </div>
}
export default function SceneSection({ isLandingPage }: { isLandingPage: boolean }) {
  const { isPc } = usePlatform() || {};
  const { currentSite = {} } = useContext(Context);
  const [sceneInfo, setSceneInfo] = useState<SceneApiData | undefined>(undefined);
  const [selectedFilter, setSelectedFilter] = useState<any>({
    agent: { key: 'all', title: '全部' },
    firstRole: { key: 'all', title: '全部' },
    secondRole: { key: 'all', title: '全部' },
  });

  useEffect(() => {
    if (!currentSite.id) return;
    const params = {
      id: currentSite.id,
      agentId: selectedFilter?.agent?.key === 'all' ? undefined : selectedFilter?.agent?.key,
      firstRoleId: selectedFilter?.firstRole?.key === 'all' ? undefined : selectedFilter?.firstRole?.key,
      secondRoleId: selectedFilter?.secondRole?.key === 'all' ? undefined : selectedFilter?.secondRole?.key,
    }
    getSceneInfo(pickBy(params, identity)).then((res: any) => {
      setSceneInfo({ ...res, sceneList: Array.isArray(res.sceneList) ? res.sceneList : []})
    });
  }, [currentSite.id, selectedFilter?.agent?.key, selectedFilter?.firstRole?.key, selectedFilter?.secondRole?.key]);

  const onClearFilter = (key?: string) => {
    if (key) {
      setSelectedFilter({ ...selectedFilter, [key]: { key: 'all', title: '全部' } });
      return;
    }
    setSelectedFilter({
      agent: { key: 'all', title: '全部' },
      firstRole: { key: 'all', title: '全部' },
      secondRole: { key: 'all', title: '全部' },
    });
  }
  return (
    <div className={isLandingPage ? undefined : 'scene-no-landing-page-wrapper'}>
      <Section title={!isLandingPage ? '所有场景' : sceneInfo?.title || ''} description={sceneInfo?.desc || ''}>
        <div className={classnames("scene-content", { mobile: !isPc })}>
          {!isLandingPage && <Filter selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} isPc={isPc}/>}
          <SceneList sceneInfo={sceneInfo} onClearFilter={onClearFilter} selectedFilter={selectedFilter} setSelectedFilter={setSelectedFilter} isLandingPage={isLandingPage}/>
        </div>
      </Section>
    </div>
  );
}